import { useState, useEffect } from 'react';
import '../../static/css/org-settings.css';

// Components
import { Typeahead } from 'react-bootstrap-typeahead';
import { CircularProgress } from '@raptormaps/progress';
import { Stack } from '@raptormaps/layout';
import OrganizationEdit from './OrganizationEdit';
import EmptyOrgUsers from './EmptyOrgUsers';
import { Text } from '@raptormaps/text';

//Models
import { Org } from '@raptormaps/raptor-models';
import type { OrgUser } from '../../api/types/OrgUser';

//API
import RestApiV2 from '../../api/RestApiV2';
import { useApi } from '../../hooks/useApi';
import { OrgsApi } from '@raptormaps/raptor-internal-client-ts';
import { usePermissions } from '../../hooks/usePermissions';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

declare global {
  interface Window {
    REACT_APP_PLANTLABELER_URL: string;
  }
}

const OrgUsersViewer = () => {
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Org | undefined>();
  const [loading, setLoading] = useState(false);

  const isSentryTabEnabled = useFeatureIsOn('org-users-sentry-permissions');
  const userIds = users?.map(u => u.auth0UserId);
  const { data: fgaUsers, isLoading: fgaUsersLoading } = usePermissions(
    selectedOrg?.id,
    userIds,
    isSentryTabEnabled,
  );

  const OrgsApiClient = useApi(OrgsApi, {
    basePath: window.REACT_APP_API_ENDPOINT_V2,
  });

  /**
   * Gets all users for the given org id
   * api - api_raptor_admin_get_org_users
   */
  const getOrgUsers = async () => {
    if (!selectedOrg) return;
    setLoading(true);

    OrgsApiClient.getOrgUsers({
      org_id: selectedOrg.id,
    })
      .then(res => {
        setUsers(res.orgUsers as OrgUser[]);
      })
      .catch(err => {
        console.log(err);
      });

    setLoading(false);
  };

  useEffect(() => {
    setUsers([]);
    getOrgUsers();
  }, [selectedOrg]);

  useEffect(() => {
    const usersWithSentryPermissions = users?.map(user => {
      const fgaUser = fgaUsers?.find(u => u.user_id === user.auth0UserId);
      return {
        ...user,
        sentryPilot: fgaUser?.object_permissions.roles?.some(
          role => role.role_id === 'sentry_command_pilot',
        ),
        sentryViewer: fgaUser?.object_permissions.roles?.some(
          role => role.role_id === 'sentry_command_viewer',
        ),
        sentryAdmin: fgaUser?.object_permissions.roles?.some(
          role => role.role_id === 'sentry_command_admin',
        ),
      };
    });

    if (selectedOrg && usersWithSentryPermissions.length > 0) {
      setUsers(usersWithSentryPermissions);
    }
  }, [fgaUsers, selectedOrg]);

  /**
   * Gets all orgs on mount
   * api - api_v2_raptor_admin_get_all_orgs
   */
  useEffect(() => {
    const api = new RestApiV2();
    api
      .get('raptor_admin/orgs', { populate: 0 })
      .then(res => {
        setOrgs(res['orgs'].map((o: object) => Org.initFromDBOrg(o)));
      })
      .catch(err => {
        console.log(err);
      });
  }, []);

  const replaceUser = (user: OrgUser) => {
    const newUsersList = [...users];
    const userIdx = users.findIndex(u => u.id === user.id);
    newUsersList[userIdx] = user;
    setUsers(newUsersList);
  };

  return (
    <div className="org-users-container">
      {/* Header with org selection */}
      <div className="header">
        <Text variant="h5">Select an organization</Text>
        <Typeahead
          id="org-typeahead"
          className="org-typeahead"
          options={Object.values(orgs)}
          placeholder="Type to search orgs..."
          disabled={loading}
          labelKey={(org: Org) => org.name}
          onChange={selected => {
            if (selected[0]) setLoading(true);
            setSelectedOrg(selected[0]);
          }}
        />
      </div>

      {/* Loading state */}
      {loading ? (
        <Stack align="center" justify="center" style={{ padding: 24 }}>
          <CircularProgress size={75} />
        </Stack>
      ) : null}

      {/* Permission Table content */}
      {!loading ? (
        <>
          {selectedOrg ? (
            <OrganizationEdit
              key="org-edit-component"
              org={selectedOrg}
              users={users}
              getOrgUsers={getOrgUsers}
              replaceUser={replaceUser}
              isLoading={fgaUsersLoading}
            />
          ) : (
            <EmptyOrgUsers />
          )}
        </>
      ) : null}
    </div>
  );
};

export default OrgUsersViewer;
